import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { FiFileText, FiMail, FiLock, FiArrowRight, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { mergeLoginPage } from './defaults';
import { getLocalized } from '../../i18n/getLocalized';
import './LoginRenderer.scss';

const FONT_STACKS = {
  'Inter': `'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif`,
  'Poppins': `'Poppins', -apple-system, sans-serif`,
  'Lora': `'Lora', Georgia, serif`,
  'Playfair Display': `'Playfair Display', 'Times New Roman', serif`,
  'JetBrains Mono': `'JetBrains Mono', 'SF Mono', Menlo, monospace`,
  'System': `-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif`,
};

function toRgba(hex, alpha) {
  if (!hex || typeof hex !== 'string') return `rgba(15, 23, 42, ${alpha})`;
  let h = hex.replace('#', '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  const num = parseInt(h, 16);
  if (Number.isNaN(num)) return `rgba(15, 23, 42, ${alpha})`;
  const r = (num >> 16) & 255;
  const g = (num >> 8) & 255;
  const b = num & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function LoginRenderer({
  branding,
  currentLanguage,
  defaultLanguage,
  email,
  password,
  onEmailChange,
  onPasswordChange,
  onSubmit,
  loading = false,
  error = null,
  onErrorDismiss,
  labels = {},
  previewMode = false,
  className = '',
}) {
  const cfg = useMemo(() => mergeLoginPage(branding?.loginPage), [branding]);
  const lang = currentLanguage || (typeof document !== 'undefined' ? document.documentElement.lang : 'en') || 'en';
  const fallbackLang = defaultLanguage || branding?.defaultLanguage || 'en';
  const localize = useCallback((v) => getLocalized(v, lang, fallbackLang), [lang, fallbackLang]);

  const companyName = localize(branding?.companyName);
  const accentColor = branding?.accentColor || '#6366f1';
  const logoUrl = branding?.logoUrl || '';
  const welcomeMessage = localize(branding?.welcomeMessage);
  const loginTagline = localize(branding?.loginTagline);
  const heroImageUrl = localize(branding?.heroImageUrl);

  const L = {
    welcomeBack: labels.welcomeBack || 'Welcome back',
    signInTo: labels.signInTo || 'Sign in to continue',
    emailAddress: labels.emailAddress || 'Email address',
    emailPlaceholder: labels.emailPlaceholder || 'you@company.com',
    password: labels.password || 'Password',
    passwordPlaceholder: labels.passwordPlaceholder || 'Enter your password',
    signIn: labels.signIn || 'Sign in',
    signingIn: labels.signingIn || 'Signing in...',
    poweredBy: labels.poweredBy || 'Powered by Nacsoft Contracts',
    defaultHeadline: labels.defaultHeadline || 'Modern Contract Management',
    defaultTagline: labels.defaultTagline || 'Streamline your contract lifecycle with powerful tools.',
    defaultFeatures: labels.defaultFeatures || [
      'End-to-end contract lifecycle',
      'Automated approval workflows',
      'Real-time collaboration',
      'Compliance & audit tracking',
    ],
  };

  const features = (cfg.features && cfg.features.length > 0)
    ? cfg.features.map(localize).filter(Boolean)
    : L.defaultFeatures;

  // Slider state — localise per-slide image, title and subtitle to current language
  const slides = useMemo(() => {
    if (cfg.backgroundStyle !== 'slider') return [];
    return (cfg.slides || [])
      .map((s) => ({
        ...s,
        imageUrl: localize(s?.imageUrl),
        title: localize(s?.title),
        subtitle: localize(s?.subtitle),
      }))
      .filter((s) => s && (s.imageUrl || s.title));
  }, [cfg.backgroundStyle, cfg.slides, localize]);

  const [slideIndex, setSlideIndex] = useState(0);
  const slideTimer = useRef(null);

  useEffect(() => {
    if (slides.length <= 1) return undefined;
    if (!cfg.sliderAutoplay && !previewMode) return undefined;
    if (previewMode && !cfg.sliderAutoplay) return undefined;
    const interval = Math.max(2000, Math.min(30000, cfg.sliderInterval || 6000));
    slideTimer.current = setInterval(() => {
      setSlideIndex((i) => (i + 1) % slides.length);
    }, interval);
    return () => clearInterval(slideTimer.current);
  }, [slides.length, cfg.sliderAutoplay, cfg.sliderInterval, previewMode]);

  useEffect(() => {
    if (slideIndex >= slides.length && slides.length > 0) setSlideIndex(0);
  }, [slides.length, slideIndex]);

  const goSlide = useCallback((dir) => {
    if (slides.length <= 1) return;
    setSlideIndex((i) => (i + dir + slides.length) % slides.length);
  }, [slides.length]);

  // Root-level CSS variables
  const rootStyle = {
    '--brand-accent': accentColor,
    '--brand-accent-soft': toRgba(accentColor, 0.12),
    '--brand-accent-softer': toRgba(accentColor, 0.06),
    '--brand-accent-strong': toRgba(accentColor, 0.85),
    '--login-font': FONT_STACKS[cfg.fontFamily] || FONT_STACKS.Inter,
    '--login-heading-weight': cfg.headingWeight || '800',
    '--login-grad-from': cfg.gradientFrom || '#4f46e5',
    '--login-grad-to': cfg.gradientTo || '#0ea5e9',
    '--login-grad-angle': `${cfg.gradientAngle || 135}deg`,
    '--login-bg-color': cfg.backgroundColor || '#0f172a',
    '--login-card-radius': {
      sharp: '4px', default: '12px', large: '20px', xl: '28px',
    }[cfg.cardRadius] || '20px',
    '--login-btn-radius': {
      sharp: '4px', default: '10px', pill: '999px',
    }[cfg.buttonRadius] || '10px',
    '--login-logo-size': {
      sm: '44px', md: '64px', lg: '96px', xl: '140px',
    }[cfg.logoSize] || '64px',
    '--login-logo-icon-size': {
      sm: '22px', md: '32px', lg: '48px', xl: '70px',
    }[cfg.logoSize] || '32px',
    '--login-logo-name-size': {
      sm: '1rem', md: '1.125rem', lg: '1.25rem', xl: '1.375rem',
    }[cfg.logoSize] || '1.125rem',
  };

  const topbarStyle = (() => {
    switch (cfg.topbarBackground) {
      case 'transparent': return { background: 'transparent', borderBottom: '1px solid transparent' };
      case 'accent':      return { background: accentColor, borderBottom: '1px solid transparent', color: '#ffffff' };
      case 'custom':      return { background: cfg.topbarCustomColor || '#ffffff' };
      case 'surface':
      default:            return {};
    }
  })();

  const classes = [
    'lr',
    `lr--layout-${cfg.layout}`,
    `lr--card-${cfg.cardPosition}`,
    `lr--theme-${cfg.theme}`,
    `lr--bg-${cfg.backgroundStyle}`,
    `lr--card-style-${cfg.cardStyle}`,
    `lr--input-${cfg.inputStyle}`,
    `lr--btn-${cfg.buttonStyle}`,
    previewMode ? 'lr--preview' : '',
    className,
  ].filter(Boolean).join(' ');

  // Build background for hero area
  const heroBgStyle = useMemo(() => {
    if (cfg.backgroundStyle === 'solid') {
      return { background: cfg.backgroundColor };
    }
    if (cfg.backgroundStyle === 'gradient') {
      return {
        background: `linear-gradient(${cfg.gradientAngle || 135}deg, ${cfg.gradientFrom || '#4f46e5'} 0%, ${cfg.gradientTo || '#0ea5e9'} 100%)`,
      };
    }
    if (cfg.backgroundStyle === 'image' && heroImageUrl) {
      return {
        // The gradient sits *under* the image: if the file 404s (deleted asset,
        // wrong host in a stored absolute URL) the panel still reads as a
        // branded hero instead of a blank white column. A CSS background gives
        // no onError hook, so the fallback has to be painted underneath.
        backgroundImage: `url(${heroImageUrl})`,
        backgroundColor: cfg.backgroundColor || '#0f172a',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      };
    }
    if (cfg.backgroundStyle === 'mesh') {
      return {
        background: cfg.backgroundColor || '#0f172a',
      };
    }
    // 'image' with no URL yet, or 'slider' with no usable slides — never leave
    // the hero transparent, which renders as an empty white half-screen.
    return {
      background: `linear-gradient(${cfg.gradientAngle || 135}deg, ${cfg.gradientFrom || '#4f46e5'} 0%, ${cfg.gradientTo || '#0ea5e9'} 100%)`,
    };
  }, [cfg, heroImageUrl]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (previewMode) return;
    if (onSubmit) onSubmit(e);
  };

  const brand = (
    <div className="lr__brand">
      {logoUrl ? (
        <img src={logoUrl} alt={companyName} className="lr__brand-logo-img" />
      ) : (
        <div className="lr__brand-logo" style={{ background: accentColor }}>
          <FiFileText />
        </div>
      )}
      {companyName && <span className="lr__brand-name">{companyName}</span>}
    </div>
  );

  const featuresList = cfg.showFeatures && features.length > 0 && (
    <ul className="lr__features">
      {features.map((text) => (
        <li key={text} className="lr__feature">
          <span className="lr__feature-dot" style={{ background: accentColor }} />
          <span>{text}</span>
        </li>
      ))}
    </ul>
  );

  const headline = welcomeMessage || L.defaultHeadline;
  const tagline = loginTagline || L.defaultTagline;

  const hero = (cfg.layout === 'split') && (
    <aside className="lr__hero" style={heroBgStyle}>
      {cfg.backgroundStyle === 'mesh' && (
        <>
          <span className="lr__mesh lr__mesh--a" style={{ background: `radial-gradient(circle, ${toRgba(cfg.gradientFrom || accentColor, 0.55)}, transparent 60%)` }} />
          <span className="lr__mesh lr__mesh--b" style={{ background: `radial-gradient(circle, ${toRgba(cfg.gradientTo || accentColor, 0.45)}, transparent 65%)` }} />
          <span className="lr__mesh lr__mesh--c" style={{ background: `radial-gradient(circle, ${toRgba(accentColor, 0.3)}, transparent 70%)` }} />
        </>
      )}
      {cfg.backgroundStyle === 'slider' && slides.length > 0 && (
        <div className={`lr__slider lr__slider--${cfg.sliderTransition}`}>
          {slides.map((slide, i) => (
            <div
              key={i}
              className={`lr__slide ${i === slideIndex ? 'lr__slide--active' : ''}`}
              style={slide.imageUrl ? {
                backgroundImage: `url(${slide.imageUrl})`,
              } : { background: `linear-gradient(${cfg.gradientAngle || 135}deg, ${cfg.gradientFrom} 0%, ${cfg.gradientTo} 100%)` }}
            >
              <div className="lr__slide-overlay" style={{ background: `rgba(10, 12, 20, ${slide.overlayOpacity ?? 0.55})` }} />
              {(slide.title || slide.subtitle) && (
                <div className="lr__slide-copy">
                  {slide.title && <h3 className="lr__slide-title">{slide.title}</h3>}
                  {slide.subtitle && <p className="lr__slide-subtitle">{slide.subtitle}</p>}
                </div>
              )}
            </div>
          ))}
          {slides.length > 1 && (
            <>
              <button type="button" className="lr__slider-nav lr__slider-nav--prev" onClick={() => goSlide(-1)} aria-label="Previous slide">
                <FiChevronLeft />
              </button>
              <button type="button" className="lr__slider-nav lr__slider-nav--next" onClick={() => goSlide(1)} aria-label="Next slide">
                <FiChevronRight />
              </button>
              <div className="lr__slider-dots">
                {slides.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    className={`lr__slider-dot ${i === slideIndex ? 'lr__slider-dot--active' : ''}`}
                    onClick={() => setSlideIndex(i)}
                    aria-label={`Go to slide ${i + 1}`}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      )}
      {cfg.backgroundStyle === 'image' && heroImageUrl && (
        <div className="lr__image-overlay" />
      )}

      <div className="lr__hero-inner">
        <div className="lr__hero-top">
          {cfg.showLogo && !cfg.showTopbar && <div className="lr__hero-brand">{brand}</div>}
        </div>
        <div className="lr__hero-copy">
          {companyName && (
            <span className="lr__hero-eyebrow" style={{ color: accentColor }}>
              <span className="lr__hero-eyebrow-dot" style={{ background: accentColor }} />
              {companyName}
            </span>
          )}
          <h1 className="lr__hero-title">{headline}</h1>
          {cfg.showTagline && <p className="lr__hero-subtitle">{tagline}</p>}
          {featuresList}
        </div>
        {cfg.showPoweredBy && (
          <div className="lr__hero-footer">{L.poweredBy}</div>
        )}
      </div>
    </aside>
  );

  const fullImageBackground = cfg.layout === 'fullImage' && (
    <div className="lr__fullbg" style={heroBgStyle}>
      {cfg.backgroundStyle === 'slider' && slides.length > 0 && slides.map((slide, i) => (
        <div
          key={i}
          className={`lr__fullbg-slide ${i === slideIndex ? 'lr__fullbg-slide--active' : ''}`}
          style={slide.imageUrl ? { backgroundImage: `url(${slide.imageUrl})` } : {}}
        />
      ))}
      <div className="lr__fullbg-overlay" />
    </div>
  );

  const card = (
    <div className="lr__card-wrap">
      <div className="lr__card">
        {cfg.layout !== 'split' && cfg.showLogo && (
          <div className="lr__card-brand">{brand}</div>
        )}
        <div className="lr__card-header">
          <h2>{L.welcomeBack}</h2>
          <p>{L.signInTo.replace('{{company}}', companyName || 'continue')}</p>
        </div>

        <form className="lr__form" onSubmit={handleSubmit}>
          {error && (
            <div className="lr__error" onClick={onErrorDismiss} role="alert">
              {error}
            </div>
          )}

          <div className="lr__field">
            <label htmlFor="lr-email">{L.emailAddress}</label>
            <div className="lr__input">
              <FiMail className="lr__input-icon" />
              <input
                id="lr-email"
                type="email"
                value={email || ''}
                onChange={(e) => onEmailChange && onEmailChange(e.target.value)}
                placeholder={L.emailPlaceholder}
                required={!previewMode}
                autoComplete="email"
                disabled={previewMode}
              />
            </div>
          </div>

          <div className="lr__field">
            <label htmlFor="lr-password">{L.password}</label>
            <div className="lr__input">
              <FiLock className="lr__input-icon" />
              <input
                id="lr-password"
                type="password"
                value={password || ''}
                onChange={(e) => onPasswordChange && onPasswordChange(e.target.value)}
                placeholder={L.passwordPlaceholder}
                required={!previewMode}
                autoComplete="current-password"
                disabled={previewMode}
              />
            </div>
          </div>

          <button
            type="submit"
            className="lr__submit"
            disabled={loading || previewMode}
          >
            {loading ? (
              <span>{L.signingIn}</span>
            ) : (
              <>
                <span>{L.signIn}</span>
                <FiArrowRight />
              </>
            )}
          </button>
        </form>

        {cfg.showPoweredBy && cfg.layout !== 'split' && (
          <div className="lr__card-footer">{L.poweredBy}</div>
        )}
      </div>
    </div>
  );

  return (
    <div className={classes} style={rootStyle}>
      {cfg.customCss && (
        <style>{`.lr.lr--custom { ${cfg.customCss} }`}</style>
      )}
      {cfg.showTopbar && (
        <header
          className={`lr__topbar lr__topbar--${cfg.topbarBackground || 'surface'}`}
          style={topbarStyle}
        >
          {brand}
        </header>
      )}
      {fullImageBackground}
      <div className="lr__body">
        {cfg.cardPosition === 'left' ? (
          <>
            {card}
            {hero}
          </>
        ) : (
          <>
            {hero}
            {card}
          </>
        )}
      </div>
    </div>
  );
}

export default LoginRenderer;
